import { Injectable } from '@angular/core';

interface CacheItem<T> {
  data: T;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})

export class CacheService {
  private cache = new Map<string, CacheItem<any>>();
  private readonly maxSize: number = 1000; // Optional max size of cache

  constructor() { }

  // Check if the cache has a specific key
  has(key: string): boolean {
    return this.cache.has(key);
  }
  
  // Method to convert hours to milliseconds
  convertHoursToMilliseconds(hours: number): number {
    return hours * 60 * 60 * 1000;
  }

  // Get the cached value by key
  get<T>(key: string): T | null {
    const item = this.cache.get(key);
    if (!item) return null;

    // Check if expired and remove it if needed
    if (this.isExpired(key, item.timestamp)) {
      this.cache.delete(key);
      return null;
    }

    return item.data;
  }

  // Set a value to the cache
  set<T>(key: string, value: T): void {
    const timestamp = Date.now();  // Capture timestamp once
    const item: CacheItem<T> = {
      data: value,
      timestamp
    };

    // Evict the oldest item if cache exceeds max size
    if (this.cache.size >= this.maxSize) {
      this.evict();
    }

    this.cache.set(key, item);
  }

  // Remove a specific key from the cache
  remove(key: string): void {
    this.cache.delete(key);
  }

  // Clear all cached data
  clear(): void {
    this.cache.clear();
    console.log("Cache Cleared!");
  }

  // Get the age of a cached item in milliseconds
  getAge(key: string): number {
    const item = this.cache.get(key);
    if (!item || !item.timestamp) {
      return Infinity;
    }
    return Date.now() - item.timestamp;
  }

  // Get all cache keys
  getKeys(): string[] {
    return Array.from(this.cache.keys());
  }

  // Check if cache is expired based on maxAge in milliseconds
  isExpired(key: string, timestamp: number, maxAge: number = this.convertHoursToMilliseconds(2)): boolean {
    return Date.now() - timestamp > maxAge;
  }

  // Evict the least recently used cache entry
  private evict(): void {
    let oldestKey: string | undefined;
    let oldestTimestamp: number = Infinity;

    for (let [key, item] of this.cache.entries()) {
      const age = Date.now() - item.timestamp;
      if (age > oldestTimestamp) {
        oldestTimestamp = age;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }
}

